import {HttpErrorResponse} from '@angular/common/http';
import {ErrorHandler, Inject, Injectable, InjectionToken, Provider} from '@angular/core';
// eslint-disable-next-line @typescript-eslint/naming-convention
import Rollbar from 'rollbar';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const RollbarService = new InjectionToken<Rollbar>('rollbar');

export interface RollbarConfig {
    accessToken: string;
    environment: string;
    enabled: boolean;
    codeVersion?: string;
}

@Injectable()
export class RollbarErrorHandler implements ErrorHandler {
    constructor(@Inject(RollbarService) private rollbar: Rollbar) {}
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    handleError(err: any): void {
        console.error(err);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const root: unknown = err?.originalError ?? err;
        // HTTP errors are reported from the HttpInterceptor, which is the single source of
        // truth for backend failures. Skipping them here avoids a duplicate Rollbar item when
        // an unhandled subscription bubbles the rethrown error up to Angular's ErrorHandler.
        if (root instanceof HttpErrorResponse) {
            return;
        }
        this.rollbar.error(...this.normalizeError(root));
    }

    private normalizeError(
        error: unknown,
    ): (string | any)[] | (Error | string)[] | (string | HttpErrorResponse)[] {
        if (error instanceof Error || typeof error === 'string') {
            return [error];
        } else if (error instanceof HttpErrorResponse) {
            return [error.message, error];
        } else {
            return ['Other error', error];
        }
    }
}

// HttpErrorResponse with status 0 means the request never received a response — typically
// a navigation/tab close cancelling the XHR, a flaky network, or a CORS preflight. These
// are not actionable bugs, so they are dropped before reaching Rollbar.
export function shouldIgnoreError(args: ArrayLike<unknown>): boolean {
    return Array.from(args).some(
        arg =>
            arg != null &&
            typeof arg === 'object' &&
            (arg as {name?: unknown}).name === 'HttpErrorResponse' &&
            (arg as {status?: unknown}).status === 0,
    );
}

export function rollbarFactory(config: RollbarConfig): Rollbar {
    return new Rollbar({
        accessToken: config.accessToken,
        captureUncaught: true,
        captureUnhandledRejections: true,
        enabled: config.enabled,
        // Keeps the Angular service worker from re-fetching the report under its install-time CSP.
        endpoint: 'https://api.rollbar.com/api/1/item/?ngsw-bypass=true',
        captureIp: false, // Set to false, in order to conform with GDPR, as recommended by Rollbar
        environment: config.environment,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payload: {client: {javascript: {code_version: config.codeVersion}}},
        checkIgnore: (_isUncaught, args) => shouldIgnoreError(args),
    });
}

export function provideRollbar(config: RollbarConfig): Provider[] {
    return [
        {provide: ErrorHandler, useClass: RollbarErrorHandler},
        {provide: RollbarService, useFactory: (): Rollbar => rollbarFactory(config)},
    ];
}
